import {set} from 'lodash';
import * as pMap from 'p-map';
import {
  BlockType,
  BoundTransformerDef,
  Entry,
  EntryDTO,
  HTMLTags,
  Node,
  NodeType,
  SerializerAttributes,
  TransformerAttributes,
  TransformerResponse,
} from "../../contract/notion";
import {Options} from "../../contract/source";
import {ContentApiDef} from "../..";
import {flatten, getAnnotatedContent, getContent, getNodeAttributes, transformBlocks} from "./helper";

// parse a Notion object to HTML
type SerializerDef = (
  self: ContentApiDef|{options: Options},
  attributes: SerializerAttributes,
  children: EntryDTO[],
  node: Node
) => string;

export const serializer: SerializerDef = (
  self,
  attributes,
  children,
  node
) => {
  // get attributes
  const {
    annotations,
    block,
    content,
    checked,
    hasMultipleNodes,
    link,
    style,
    source,
    type,
    tag
  } = getNodeAttributes(node, attributes);

  // render HTML tags with content
  const annotator = getAnnotatedContent.bind(null, annotations, children);
  const render = getContent.bind(
    null,
    block,
    {tag, type, hasMultipleNodes, checked, source, link, style},
    annotator,
  );

  return render(content);
}

// transform a list of blocks
type TransformerDef = (
  self: ContentApiDef|{options: Options},
  attributes: TransformerAttributes,
  nodes: Node[],
  children: Node[]
) => TransformerResponse;

export const transformer: TransformerDef = (
  self,
  attributes,
  nodes = [],
  children = []
) => {
  let serialize;
  if (self) {
    const {serializer: s} = self.options.get('content.transformer');
    serialize = s || serializer.bind(null, self);
  } else {
    serialize = serializer.bind(null, self);
  }

  // attributes
  const {previous, next, type} = attributes;
  attributes.hasMultipleNodes = nodes.length > 1;
  if (type === BlockType.BULLETED_LIST_ITEM || type === BlockType.NUMBERED_LIST_ITEM) {
    attributes.shouldOpenParent = (!previous || previous.type !== type) && type.split('_item')[0] as BlockType;
    attributes.shouldCloseParent = (!next || next.type !== type) && type.split('_item')[0] as BlockType;
  } else if (type === BlockType.TOGGLE) {
    attributes.shouldOpenParent = (!previous || previous.type !== type) && type as BlockType;
    attributes.shouldCloseParent = (!next || next.type !== type) && type as BlockType;
    attributes.type = NodeType.TOGGLE_ITEM as any; // NodeType to BlockType
  } else if (type === BlockType.TO_DO) {
    attributes.shouldOpenParent = (!previous || previous.type !== type) && type as BlockType;
    attributes.shouldCloseParent = (!next || next.type !== type) && type as BlockType;
    attributes.type = NodeType.TO_DO_ITEM as any; // NodeType to BlockType
  }

  // serialize
  const items: string[] = (nodes || []).map((node: Node, index: number) => {
    node.type = (!attributes.hasMultipleNodes && attributes.type) as any || node.type; // NodeType to BlockType

    attributes.index = (attributes.index || 0) + index;
    if (attributes.hasMultipleNodes) {
      // complex node with inline nodes, e.g. anchor
      attributes.shouldOpen = !index && attributes.type;
      attributes.shouldClose = (index === nodes.length - 1) && attributes.type;
    }

    const {hasMultipleNodes, shouldOpen, shouldOpenParent, shouldClose, shouldCloseParent} = attributes;

    const str = [];
    if ((!hasMultipleNodes || shouldOpen) && shouldOpenParent) {
      str.push(`<${HTMLTags[shouldOpenParent.toUpperCase() as keyof typeof HTMLTags]}>`);
    }
    if (shouldOpen) str.push(`<${HTMLTags[shouldOpen.toUpperCase() as keyof typeof HTMLTags]}>`);

    str.push(serialize(attributes, children, node, index));

    if (shouldClose) str.push(`</${HTMLTags[shouldClose.toUpperCase() as keyof typeof HTMLTags]}>`);
    if ((!hasMultipleNodes || shouldClose) && shouldCloseParent) {
      str.push(`</${HTMLTags[shouldCloseParent.toUpperCase() as keyof typeof HTMLTags]}>`);
    }

    return str.join('');
  }).filter((i: string): string => i);

  // timestamps
  const {created_time, last_edited_time} = attributes;

  return {
    items,
    attributes: {
      ...attributes,
      created_time: new Date(created_time).toDateString(),
      last_edited_time: new Date(last_edited_time).toDateString(),
    }
  };
}

// render HTML from a list of Notion Block objects
export type TransformContentDef = (
  self: ContentApiDef|{options: Options},
  transform: BoundTransformerDef,
  serialize: SerializerDef,
  entry: Entry
) => Promise<Entry>;

export const transformContent: TransformContentDef = async (
  self, // could not assign 'this' :(
  transform,
  serialize,
  entry,
) => {
  if (!transform || !serialize) return;
  const {nodes: blocks} = entry;

  // share serializer instance with transformer
  set(self.options, 'content.transformer.serializer', serialize);

  // transform blocks
  const content = await pMap(blocks || [], transformBlocks.bind(null, self, transform, blocks), {concurrency: 3});
  const results = content.filter(n => n);

  return {
    ...entry,
    content: flatten(results),
    results
  }
}

export default (
  self: ContentApiDef|{options: Options}
): TransformContentDef =>
  transformContent.bind(
    null,
    self, // could not assign 'this' :(
    transformer.bind(null, self),
    serializer.bind(null, self)
  );
